import { Editor, Node, Text, Transforms } from 'slate'
import { jsx } from 'slate-hyperscript'
import escapeHtml from 'escape-html'

import { getList, handleLineBreak, isListNode } from './lists'
import { insertLink, isValidHref } from './links'

export const BASE_BLOCK_ELEMENT_TYPES = ['paragraph', 'headline1', 'headline2', 'headline3']
export const LIST_TYPES = ['ordered-list', 'unordered-list']
export const ALIGNABLE_TYPES = ['paragraph', 'headline1', 'headline2', 'headline3', 'list-item-child', 'button']

export function parseHtml(html) {
  const parsedHtml = new DOMParser().parseFromString(html, 'text/html')
  const deserializedParsedHtml = deserialize(parsedHtml.body)

  return deserializedParsedHtml
}

export function serialize(nodes) {
  function serializeNode(node) {
    if (Text.isText(node)) {
      if (!node.text) return ''

      let result = escapeHtml(node.text)
      if (node.bold) result = `<strong>${result}</strong>`
      if (node.underline) result = `<u>${result}</u>`
      if (node.italic) result = `<em>${result}</em>`
      if (node.color) result = `<span style="color:${node.color}">${result}</span>`

      return result
    }

    const children = node.children.map((n) => serializeNode(n)).join('')

    const styleRules = []
    if (node.align) styleRules.push(`text-align:${node.align}`)
    if (node.color) styleRules.push(`color:${node.color}`)

    const style = styleRules.length > 0 ? ` style="${styleRules.join(';')}"` : ''

    switch (node.type) {
      case 'paragraph':
        return `<p${style}>${children}</p>`
      case 'headline1':
        return `<h1${style}>${children}</h1>`
      case 'headline2':
        return `<h2${style}>${children}</h2>`
      case 'headline3':
        return `<h3${style}>${children}</h3>`
      case 'blockquote':
        return `<blockquote${style}>${children}</blockquote>`
      case 'list-item':
        return `<li${style}>${children}</li>`
      case 'list-item-child':
        return `<div${style}>${children}</div>`
      case 'ordered-list':
        return `<ol${style}>${children}</ol>`
      case 'unordered-list':
        return `<ul${style}>${children}</ul>`
      case 'link': {
        if (!isValidHref(node.url)) return children

        let linkAttributes = `href="${node.url}"`
        if (node.isExternal) linkAttributes += ' target="_blank" rel="noopener noreferrer"'
        linkAttributes += style

        return `<a ${linkAttributes}>${children}</a>`
      }

      case 'button': {
        let buttonAttributes = `href="${node.url}"`
        if (node.isExternal) buttonAttributes += ' target="_blank" rel="noopener noreferrer"'
        buttonAttributes += ' class="rich-text-button"'

        return `<div ${style}><a ${buttonAttributes}>${children}</a></div>`
      }

      default:
        return children
    }
  }

  return nodes.map(serializeNode).join('')
}

const ELEMENT_TAGS = {
  P: () => ({ type: 'paragraph' }),
  H1: () => ({ type: 'headline1' }),
  H2: () => ({ type: 'headline2' }),
  H3: () => ({ type: 'headline3' }),
  BLOCKQUOTE: () => ({ type: 'blockquote' }),
  LI: () => ({ type: 'list-item' }),
  DIV: (el) => {
    const isButton = el.childNodes.length === 1 && !hasTagName(el.parentNode, 'li') && el.firstChild?.nodeName === 'A'
    if (isButton) {
      return {
        type: 'button',
        url: el.firstChild.getAttribute('href'),
        isExternal: el.firstChild.getAttribute('target'),
      }
    }

    return { type: 'list-item-child' }
  },
  OL: () => ({ type: 'ordered-list' }),
  UL: () => ({ type: 'unordered-list' }),
  A: (el) => ({
    type: 'link',
    url: el.getAttribute('href'),
    isExternal: el.target,
  }),
}

const TEXT_TAGS = {
  STRONG: () => ({ bold: true }),
  EM: () => ({ italic: true }),
  U: () => ({ underline: true }),
  SPAN: (el) => ({ color: el.style.color }),
}

const SUPPORTED_TAGS = [...Object.keys(ELEMENT_TAGS), ...Object.keys(TEXT_TAGS), 'BODY']

export function deserialize(el) {
  const isFirstLevelNode = el.parentNode && el.parentNode.nodeName === 'BODY'
  const isOnlyChild = el.parentNode.childNodes.length === 1
  const isSupported = SUPPORTED_TAGS.includes(el.nodeName)

  // Handle text nodes
  if (el.nodeType === 3) {
    const isEmpty = el.textContent.trim().length === 0

    if (isFirstLevelNode && isEmpty) return null
    if (isFirstLevelNode) return jsx('element', { type: 'paragraph' }, el.textContent.replace(/\n/g, ' '))
    return el.textContent.replace(/\n/g, ' ')
  }
  // Handle nodes that are not supported
  if (!isSupported) {
    // Return an empty paragraph if this node is the only one in the body.
    if (isFirstLevelNode && isOnlyChild) return jsx('element', { type: 'paragraph' }, '')
    // Remove node when it is on the first level.
    else if (isFirstLevelNode) return null
    // Turn `b` tag into `strong` tag.
    if (el.nodeName === 'B' && el.textContent) return jsx('text', TEXT_TAGS.STRONG(el), el.textContent)
    // If we get here, this node is a child of a supported node. In this case,
    // we return an empty string in order to avoid the parent node being empty.
    return ''
  }

  const isButton = ELEMENT_TAGS.DIV(el).type === 'button'
  const isValidFirstLevelElement = ['P', 'H1', 'H2', 'H3', 'BLOCKQUOTE', 'OL', 'UL'].includes(el.nodeName) || isButton
  const validParagraphChildTags = [...Object.keys(TEXT_TAGS), 'A']
  if (isFirstLevelNode && !isValidFirstLevelElement) {
    // If possible, wrap the element in a paragraph and continue deserialization.
    if (validParagraphChildTags.includes(el.tagName)) {
      const paragraphElement = document.createElement('p')
      el.parentNode.insertBefore(paragraphElement, el)
      paragraphElement.appendChild(el)

      return deserialize(paragraphElement)
    }

    // If the element cannot be wrapped inside a paragraph, wrap its children in one.
    const children = Array.from(el.childNodes).map((node) => {
      // If it‘s a paragraph, take its children. A paragraph is not a valid
      // child node of a paragraph and will cause the element to disappear.
      if (hasTagName(node, 'p')) return Array.from(node.childNodes).map(deserialize)

      return deserialize(node)
    })

    if (children.length === 0) children.push({ text: '' })

    return jsx('element', { type: 'paragraph' }, children)
  }

  // In our previous scribe implementation, when nesting list elements (`ul` or `ol`)
  // inside other list elements, we nested them as direct children.
  //
  // Previously (wrong):
  // <ul>
  //   <li>
  //     <div>Foo</div>
  //   </li>
  //   <ul></ul>
  // </ul>
  //
  // In our current implementation, we do it correctly:
  // <ul>
  //   <li>
  //     <div>Foo</div>
  //     <ul></ul>
  //   </li>
  // </ul>
  //
  // The following detects this invalid structure and reorders it correctly.
  if (isListElement(el)) {
    // if the list is already nested inside a list, just contine with its children
    if (isListElement(el.parentNode)) {
      return Array.from(el.childNodes).map(deserialize)
    }

    Array.from(el.childNodes).forEach((childNode) => {
      if (isListElement(childNode)) {
        const prevSibling = findPreviousSiblingElement(childNode)
        if (prevSibling) prevSibling.appendChild(childNode)
        else {
          const nextSibling = findNextSiblingElement(childNode)
          if (nextSibling) nextSibling.appendChild(childNode)
        }
      }
    })
  }

  // In our slate structure, `li` text is always nested inside a `div`.
  // In our previous scribe implementation, that was not the case.
  // The following finds those unnested nodes and wraps them in a `div`.
  if (hasTagName(el, 'li')) {
    const invalidChildNodes = Array.from(el.childNodes).filter((node) => !hasTagName(node, 'div', 'ul', 'ol'))
    invalidChildNodes.forEach((childNode) => {
      if (childNode.previousSibling && hasTagName(childNode.previousSibling, 'div')) {
        childNode.previousSibling.appendChild(childNode)
      } else {
        const divWrapper = document.createElement('div')
        el.insertBefore(divWrapper, childNode)
        divWrapper.appendChild(childNode)
      }
    })
  }

  // Slate does not allow nesting `ol` and `ul` in the same `li` parent.
  // The following detects those occurrences and merges their children under
  // a single parent (`ol` or `ul` depending on what came first).
  if (hasTagName(el, 'li')) {
    let nestedList = null
    Array.from(el.childNodes)
      .filter(isListElement)
      .forEach((childNode) => {
        if (nestedList) {
          Array.from(childNode.childNodes).forEach((nestedChildNode) => {
            nestedList.appendChild(nestedChildNode)
          })
          el.removeChild(childNode)
        } else {
          nestedList = childNode
        }
      })
  }

  const children = Array.from(el.childNodes).map(deserialize)

  if (el.nodeName === 'BODY') {
    // If we find an empty body we return an empty paragraph. Otherwise slate would complain about
    // having no children.
    if (children.length === 0) return [{ type: 'paragraph', children: [{ text: '' }] }]

    return jsx('fragment', {}, children)
  }

  if (ELEMENT_TAGS[el.nodeName]) {
    let attrs = ELEMENT_TAGS[el.nodeName](el)
    const isButton = attrs.type === 'button'

    const styleData = {}
    if (el.align) styleData.align = el.align
    if (el.style && el.style.textAlign) styleData.align = el.style.textAlign

    if (el.color) styleData.color = el.color
    if (el.style && el.style.color) styleData.color = el.style.color

    // An empty element should have a text element nested inside it (the slate editor breaks otherwise).
    if (children.length === 0) children.push(jsx('text', {}, ''))

    // Catch invalid div element (not a direct child of li element).
    if (hasTagName(el, 'div') && !isButton && (!el.parentNode || !hasTagName(el.parentNode, 'li'))) {
      // Turn div into paragraph when it has no valid parent.
      if (isFirstLevelNode) attrs = ELEMENT_TAGS.P(el)

      return children
    }

    if (isButton) {
      const aTagChildren = Array.from(el.firstChild.childNodes).map(deserialize)

      // An empty element should have a text element nested inside it (the slate editor breaks otherwise).
      if (aTagChildren.length === 0) aTagChildren.push(jsx('text', {}, ''))

      return jsx('element', { ...attrs, ...styleData }, aTagChildren)
    }

    const deserializedElement = jsx('element', { ...attrs, ...styleData }, children)
    return deserializedElement
  }

  if (TEXT_TAGS[el.nodeName]) {
    const attrs = TEXT_TAGS[el.nodeName](el)

    // Text elements need to have at least one child (the slate editor breaks otherwise).
    if (children.length === 0) return jsx('text', attrs, '')

    // Depending on the nesting structure each child could be an object, string or array.
    // A child passed to `jsx('text')` must only contain a single node's worth of children.
    // Flattening the array takes care of this being the case.
    return children.flat().map((child) => {
      // A link is not a valid child of a text tag. Instead the attributes should be passed
      // down to the children.
      if (child.type === 'link' || child.type === 'button') {
        child.children = child.children.map((el) => ({ ...attrs, ...el }))
        return child
      }
      return jsx('text', attrs, child)
    })
  }

  return children
}

export function withPasting(editor) {
  const { insertData } = editor

  editor.insertData = (data) => {
    if (data.types.includes('application/x-slate-fragment')) {
      const fragment = data.getData('application/x-slate-fragment')
      const decoded = decodeURIComponent(window.atob(fragment))
      const parsed = JSON.parse(decoded)
      const { isList } = getList(editor)

      const nodesHaveList = Boolean(parsed.find(isListNode))

      if (isList) {
        // We need to so some legwork to merge slate fragments into a list.
        const pasteNodeIntoList = (node, index) => {
          if (index !== 0) handleLineBreak(editor)
          if (node.type === 'list-item') {
            pasteNodeIntoList(node.children[0], 0)
            if (node.children[1]) Editor.insertNode(editor, node.children[1])
          } else if (isListNode(node) || node.type === 'blockquote') {
            node.children.forEach(pasteNodeIntoList)
          } else {
            Editor.insertNode(editor, node.children)
          }
        }

        parsed.forEach(pasteNodeIntoList)
      } else if (nodesHaveList) {
        parsed.forEach((node, index) => {
          const [sameTypeMatch] = Editor.nodes(editor, { match: (n) => n.type === node.type })
          const isSameType = Boolean(sameTypeMatch)

          const [baseBlockElementMatch] = Editor.nodes(editor, {
            match: (n) => BASE_BLOCK_ELEMENT_TYPES.includes(n.type),
          })
          const isBaseBlockElement = Boolean(baseBlockElementMatch)

          if (index === 0 && isSameType) Editor.insertNode(editor, node.children)
          else Editor.insertNode(editor, node)

          // Removing the empty base block element if we pasted a list into it.
          if (isListNode(node) && isBaseBlockElement && !Node.string(baseBlockElementMatch[0])) {
            Transforms.removeNodes(editor, { at: baseBlockElementMatch[1] })
          }
        })
      } else {
        // When pasting into an empty line, use the type of the first pasted node.
        if (editor.isEmptyLine(editor)) Transforms.setNodes(editor, parsed[0])

        insertData(data)
      }
    } else {
      const text = data.getData('text/plain')

      if (text && isValidHref(text)) {
        insertLink(editor, text)
      } else if (text) {
        const { isList } = getList(editor)

        // Turning plain text into HTML by splitting it at line breaks.
        const lineBreakRegex = /(\r\n|\r|\n)/gm

        text.split(lineBreakRegex).forEach((text) => {
          if (lineBreakRegex.test(text)) {
            if (isList) handleLineBreak(editor)
            else Editor.insertBreak(editor)
          } else {
            Editor.insertNode(editor, [{ text }])
          }
        })
      } else {
        insertData(data)
      }
    }
  }

  return editor
}

// When pasting into the headline field, we want to paste the content as plain text but keep the
// text formatting available such as bold, italic, underline, color and alignment.
export function withHeadlinePasting(editor) {
  const { insertData } = editor

  editor.insertData = (data) => {
    if (data.types.includes('application/x-slate-fragment')) {
      const fragment = data.getData('application/x-slate-fragment')
      const decoded = decodeURIComponent(window.atob(fragment))
      const parsed = JSON.parse(decoded)

      const processNode = (node, index) => {
        node.children.forEach((child) => {
          if (child.children) {
            // If the child has its own children, process it as a node
            processNode(child)
          } else {
            // Otherwise, insert the child as a text node
            const { text, bold, italic, underline, color } = child
            const nodeToInsert = { text, bold, italic, underline, color }
            Editor.insertNode(editor, [nodeToInsert])

            // Take over the alignment of the first pasted node
            if (index === 0) {
              const align = node.align
              if (align) {
                Transforms.setNodes(editor, { align }, { at: editor.selection })
              }
            }
          }
        })
      }

      parsed.forEach(processNode)
    } else {
      insertData(data)
    }
  }
  return editor
}

// helpers

function hasTagName(node, ...tagNames) {
  return (
    Boolean(node && node.tagName) && tagNames.some((tagName) => tagName.toLowerCase() === node.tagName.toLowerCase())
  )
}

function isListElement(node) {
  return hasTagName(node, 'ul', 'ol')
}

function findPreviousSiblingElement(node) {
  if (node.previousSibling) {
    if (node.previousSibling.nodeType === 1) return node.previousSibling
    else return findPreviousSiblingElement(node.previousSibling)
  }
  return null
}

function findNextSiblingElement(node) {
  if (node.nextSibling) {
    if (node.nextSibling.nodeType === 1) return node.nextSibling
    else return findNextSiblingElement(node.nextSibling)
  }
  return null
}
